import DOMPurify from 'dompurify';

/**
 * Sanitiza input HTML para prevenir ataques XSS
 * @param dirty - String potencialmente peligroso
 * @returns String sanitizado seguro
 */
export const sanitizeHTML = (dirty: string): string => {
  if (typeof window === 'undefined') {
    // Server-side fallback - strip all HTML tags
    return dirty.replace(/<[^>]*>/g, '');
  }
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [], // No permitir ningún tag HTML
    ALLOWED_ATTR: [], // No permitir ningún atributo
  });
};

/**
 * Sanitiza input de texto básico removiendo caracteres peligrosos
 * @param input - String a sanitizar
 * @returns String sanitizado
 */
export const sanitizeText = (input: string): string => {
  if (!input) return '';
  
  // Remover caracteres de control y caracteres especiales peligrosos
  return input
    .replace(/[\x00-\x1F\x7F]/g, '') // Control characters
    .replace(/[<>]/g, '') // Brackets que podrían ser usados para HTML
    .trim();
};

/**
 * Sanitiza email - ya validado por Zod, solo limpieza adicional
 * @param email - Email a sanitizar
 * @returns Email sanitizado
 */
export const sanitizeEmail = (email: string): string => {
  return email.toLowerCase().trim();
};

/**
 * Sanitiza número de teléfono - solo números y caracteres permitidos
 * @param phone - Teléfono a sanitizar
 * @returns Teléfono sanitizado
 */
export const sanitizePhone = (phone: string): string => {
  return phone.replace(/[^\d\s\-\(\)\+]/g, '').trim();
};

/**
 * Valida y sanitiza URL
 * @param url - URL a validar
 * @returns URL sanitizada o null si es inválida
 */
export const sanitizeURL = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    // Solo permitir protocolos seguros
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
};

/**
 * Sanitiza objeto completo recursivamente
 * @param obj - Objeto a sanitizar
 * @returns Objeto sanitizado
 */
export const sanitizeObject = <T extends Record<string, any>>(obj: T): T => {
  const sanitized = {} as T;
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key as keyof T] = sanitizeText(value) as any;
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key as keyof T] = sanitizeObject(value);
    } else {
      sanitized[key as keyof T] = value;
    }
  }
  
  return sanitized;
};

/**
 * Valida que un string no contenga SQL injection patterns
 * @param input - String a validar
 * @returns true si es seguro
 */
export const isSafeSQLString = (input: string): boolean => {
  const sqlInjectionPattern = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|DECLARE)\b)|(-{2})|([;'])/gi;
  return !sqlInjectionPattern.test(input);
};

/**
 * Valida longitud de string dentro de límites razonables
 * @param input - String a validar
 * @param maxLength - Longitud máxima permitida
 * @returns true si está dentro del límite
 */
export const isValidLength = (input: string, maxLength: number): boolean => {
  return input.length <= maxLength && input.length > 0;
};

/**
 * Rate limiting simple basado en localStorage para formularios públicos
 * @param key - Identificador único para el rate limit
 * @param maxAttempts - Número máximo de intentos
 * @param windowMs - Ventana de tiempo en milisegundos
 * @returns true si el rate limit permite la acción
 */
export const checkRateLimit = (
  key: string,
  maxAttempts: number = 3,
  windowMs: number = 60000 // 1 minuto por defecto
): boolean => {
  if (typeof window === 'undefined') return true;
  
  const now = Date.now();
  const rateLimitKey = `rate_limit_${key}`;
  const storedData = localStorage.getItem(rateLimitKey);
  
  if (!storedData) {
    localStorage.setItem(rateLimitKey, JSON.stringify({ count: 1, timestamp: now }));
    return true;
  }
  
  const { count, timestamp } = JSON.parse(storedData);
  
  // Si la ventana de tiempo ha pasado, resetear
  if (now - timestamp > windowMs) {
    localStorage.setItem(rateLimitKey, JSON.stringify({ count: 1, timestamp: now }));
    return true;
  }
  
  // Si estamos dentro de la ventana, verificar el contador
  if (count >= maxAttempts) {
    return false;
  }
  
  // Incrementar contador
  localStorage.setItem(rateLimitKey, JSON.stringify({ count: count + 1, timestamp }));
  return true;
};

/**
 * Limpia rate limit para una key específica
 * @param key - Identificador del rate limit a limpiar
 */
export const clearRateLimit = (key: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(`rate_limit_${key}`);
};
